import React, { useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from "../../../config/firebase";
import { getCookie, setCookie } from '../../../utils/local_storing';

// Renders the Action Layer's deterministic "what can I do next" buttons
// (view_similar_products, compare_products, track_order, ...) below a
// product carousel. Clicking one sends its label into the chat exactly like
// DefaultMessages/ProductDisplay's existing quick-reply buttons do -- push a
// user message to Firestore, then GET the bot server so it replies to it.
// The caller (chatroom.js) only ever mounts this on the most recent message,
// so it naturally disappears once the conversation moves past this turn --
// this component only needs to handle the gap between a click and that next
// Firestore update landing, via the `dismissed` flag below.
function ActionButtons({ actions, isLoading, setIsLoading }) {
  const [dismissed, setDismissed] = useState(false);

  if (!Array.isArray(actions) || actions.length === 0 || dismissed) {
    return null;
  }

  // Mirrors the shop_id -> bot server routing committed in chatfooter.js.
  const buildBotUrl = (shopId, docid, actionText) => {
    if (shopId === "null") {
      return 'http://127.0.0.1:8080/server?docid=' + docid + "&shop_id=yaan-man.myshopify.com&user_message=" + encodeURIComponent(actionText);
    }
    if (shopId === "Krunk.ai") {
      return "https://krunk-demo.agreeableplant-c6fa5f15.centralindia.azurecontainerapps.io/server?docid=" + docid + "&shop_id=Krunk.ai";
    }
    if (shopId === "headsupfortails.myshopify.com") {
      return "https://modular-bot-371280248581.asia-south1.run.app/server?docid=" + docid
        + "&shop_id=headsupfortails.myshopify.com&user_message=" + encodeURIComponent(actionText);
    
      }
    return "https://modular-bot-371280248581.asia-south1.run.app/server?docid=" + docid
      + "&shop_id=" + encodeURIComponent(shopId) + "&user_message=" + encodeURIComponent(actionText);
  };

  const getCurrentTimeString = () => {
    const currentTime = new Date();
    let hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
  };

  const handleActionClick = async (action) => {
    if (isLoading) return;

    const actionText = action?.label || action?.action_id;
    if (!actionText) return;

    // Hide immediately -- don't wait for the Firestore round trip below.
    setDismissed(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const currentDomain = urlParams.get("parentUrl");
      const shopId = currentDomain || "null";
      const uidvar = currentDomain + "uid";
      const uid = getCookie(uidvar) || "";

      const currentTimeString = getCurrentTimeString();
      const currentTime = new Date();
      const seconds = currentTime.getSeconds();
      const month = String(currentTime.getMonth() + 1).padStart(2, "0");
      const day = String(currentTime.getDate()).padStart(2, "0");
      const year = currentTime.getFullYear();
      const formattedHours = String(currentTime.getHours() % 12 || 12).padStart(2, "0");
      const formattedMinutes = String(currentTime.getMinutes()).padStart(2, "0");
      const date_string = `${month}/${day}/${year}, ${formattedHours}:${formattedMinutes}:${seconds}`;

      const data_var = currentDomain + "myData";
      const storedData = getCookie(data_var);
      let docid = "";

      if (!storedData) {
        const initialData = { key: uid + Date.now().toString() };
        setCookie(data_var, JSON.stringify(initialData));
        docid = initialData.key;
      } else {
        docid = JSON.parse(storedData).key;
      }

      const docRef = doc(db, "Messages", docid);
      const docSnap = await getDoc(docRef);
      let assigned_to = "bot@krunk.ai";

      if (docSnap.exists()) {
        const message = docSnap.data().Messages;
        assigned_to = docSnap.data().assigned_to || "bot@krunk.ai";
        message.push({ user: actionText, user_timestamp: date_string, time: currentTimeString });

        await setDoc(docRef, {
          Messages: message,
          server: false,
          user_id: docid,
          shop_id: shopId,
          User_Channel: "Web",
          timestamp: Date.now(),
          last_updated: serverTimestamp(),
        }, { merge: true });
      }

      if (assigned_to === "bot@krunk.ai") {
        setIsLoading(true);
        const url = buildBotUrl(shopId, docid, actionText);

        fetch(url, { method: 'GET' })
          .then(() => setIsLoading(false))
          .catch(() => setIsLoading(false));
      }
    } catch (error) {
      console.error("Error handling action button click:", error);
    }
  };

  return (
    <div className="krunk-flex krunk-flex-wrap krunk-justify-center krunk-gap-2 krunk-mt-3 krunk-mb-1">
      {actions.map((action) => (
        <button
          key={action.action_id}
          type="button"
          onClick={() => handleActionClick(action)}
          disabled={isLoading}
          className="krunk-px-3 krunk-py-1.5 krunk-rounded-full krunk-text-xs krunk-font-medium krunk-border krunk-bg-white krunk-whitespace-nowrap krunk-transition-opacity hover:krunk-opacity-80"
          style={{
            borderColor: 'var(--color-buttonbackground)',
            color: 'var(--color-buttonbackground)',
            opacity: isLoading ? 0.5 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {action.label || action.action_id}
        </button>
      ))}
    </div>
  );
}

export default ActionButtons;
